import axios from 'axios'
import env from '@utils/env'
import { API_ERROR_EVENT, API_OK_EVENT } from '@components/ApiErrorBanner'

const API_BASE_URL = env.apiUrl

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error)
)

let _refreshing   = null
let _serverDown   = false

const fireDown = () => { if (!_serverDown) { _serverDown = true;  window.dispatchEvent(new Event(API_ERROR_EVENT)) } }
const fireUp   = () => { if  (_serverDown) { _serverDown = false; window.dispatchEvent(new Event(API_OK_EVENT))    } }

window.addEventListener('api:retry', () => {
  api.get('/health').then(fireUp).catch(() => {})
})

api.interceptors.response.use(
  (response) => { fireUp(); return response.data },
  async (error) => {
    const original = error.config
    const status   = error.response?.status
    const code     = error.response?.data?.code

    const isAdminRoute = original?.url?.includes('/admin')
    const alreadyRetried = original?._retry

    if (status === 401 && isAdminRoute && !alreadyRetried && original?.url !== '/admin/refresh') {
      original._retry = true
      try {
        if (!_refreshing) {
          _refreshing = api.post('/admin/refresh').finally(() => { _refreshing = null })
        }
        const refreshed = await _refreshing
        if (refreshed?.data?.admin) {
          localStorage.setItem('adminUser', JSON.stringify(refreshed.data.admin))
        }
        return api(original)
      } catch {
        localStorage.removeItem('adminUser')
        window.location.href = '/admin/login'
      }
    }

    const isHealthCheck = original?.url === '/health'
    if (!isHealthCheck && (!error.response || error.response.status >= 500)) {
      fireDown()
    }

    // Flatten the error to the server's response body for consumers, but
    // preserve the HTTP status so callers can distinguish 401 / 429 / etc.
    // Rate-limit (429) responses arrive as a plain string body, so wrap those.
    if (error.response) {
      const data = error.response.data
      const payload = (data && typeof data === 'object') ? { ...data } : { message: data }
      payload.status = error.response.status
      return Promise.reject(payload)
    }
    return Promise.reject(error)
  }
)

// API endpoints
export const coursesAPI = {
  getAll: (params) => api.get('/courses', { params }),
  getById: (id) => api.get(`/courses/${id}`),
  getBySlug: (slug) => api.get(`/courses/${slug}`),
  getFeatured: (limit) => api.get('/courses/featured/list', { params: { limit } }),
  getPopular: (limit) => api.get('/courses/popular/list', { params: { limit } }),
  getRelated: (id, limit) => api.get(`/courses/${id}/related`, { params: { limit } }),
  getCategories: () => api.get('/courses/categories/list'),
  create: (data) => api.post('/courses', data),
  update: (id, data) => api.put(`/courses/${id}`, data),
  delete: (id) => api.delete(`/courses/${id}`),
}

export const batchesAPI = {
  getAll: (params) => api.get('/batches', { params }),
  getById: (id) => api.get(`/batches/${id}`),
  getUpcoming: (limit) => api.get('/batches/upcoming/list', { params: { limit } }),
  getByCourse: (courseId, status) => api.get(`/batches/course/${courseId}`, { params: { status } }),
  enroll: (id, data) => api.post(`/batches/${id}/enroll`, data),
  create: (data) => api.post('/batches', data),
  update: (id, data) => api.put(`/batches/${id}`, data),
  delete: (id) => api.delete(`/batches/${id}`),
  addAnnouncement: (id, data) => api.post(`/batches/${id}/announcements`, data),
  updateEnrollment: (id, studentId, data) => api.put(`/batches/${id}/enrollment/${studentId}`, data),
}

export const leadsAPI = {
  getAll: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  addNote: (id, data) => api.post(`/leads/${id}/notes`, data),
  addActivity: (id, data) => api.post(`/leads/${id}/activities`, data),
  convert: (id, data) => api.post(`/leads/${id}/convert`, data),
  getStats: () => api.get('/leads/stats/dashboard'),
}

export const testimonialsAPI = {
  getAll: (params) => api.get('/testimonials', { params }),
  getById: (id) => api.get(`/testimonials/${id}`),
  getFeatured: (limit) => api.get('/testimonials/featured/list', { params: { limit } }),
  getByCourse: (courseId, limit) => api.get(`/testimonials/course/${courseId}`, { params: { limit } }),
  getSuccessStories: (limit) => api.get('/testimonials/placement/success-stories', { params: { limit } }),
  getVideos: (limit) => api.get('/testimonials/video/list', { params: { limit } }),
  create: (data) => api.post('/testimonials', data),
  update: (id, data) => api.put(`/testimonials/${id}`, data),
  delete: (id) => api.delete(`/testimonials/${id}`),
  approve: (id, data) => api.put(`/testimonials/${id}/approve`, data),
  reject: (id) => api.put(`/testimonials/${id}/reject`),
  toggleFeatured: (id) => api.put(`/testimonials/${id}/featured`),
  getStats: () => api.get('/testimonials/stats/dashboard'),
}

export const adminAPI = {
  getSetupStatus: () => api.get('/admin/setup/status'),
  createFirstAdmin: (data) => api.post('/admin/setup/first-admin', data),
  login: (data) => api.post('/admin/login', data),
  logout: () => api.post('/admin/logout'),
  refresh: () => api.post('/admin/refresh'),
  me: () => api.get('/admin/me'),
  getDashboardStats: () => api.get('/admin/dashboard/stats'),
  getAll: () => api.get('/admin/admins'),
  create: (data) => api.post('/admin/admins', data),
  update: (id, data) => api.put(`/admin/admins/${id}`, data),
  deactivate: (id) => api.put(`/admin/admins/${id}/deactivate`),
  updateProfile:  (data) => api.put('/admin/profile', data),
  uploadAvatar:   (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/admin/avatar', fd) },
  changePassword: (data) => api.put('/admin/change-password', data),
  getPerformance: (id) => api.get(`/admin/performance/${id}`),
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
}

export const settingsAPI = {
  getPublic: () => api.get('/settings'),
  get: () => api.get('/admin/settings'),
  update: (data) => api.put('/admin/settings', data),
  uploadBranding: (type, file) => {
    const fd = new FormData()
    fd.append('file', file)
    return api.post(`/admin/settings/upload/${type}`, fd)
  },
}

export const contactsAPI = {
  submit:       (data)    => api.post('/contacts', data),
  getAll:       (params)  => api.get('/contacts', { params }),
  getById:      (id)      => api.get(`/contacts/${id}`),
  update:       (id, data)=> api.put(`/contacts/${id}`, data),
  delete:       (id)      => api.delete(`/contacts/${id}`),
  getStats:     ()        => api.get('/contacts/stats/summary'),
  blockEmail:   (email)   => api.post('/contacts/block/email',   { email }),
  unblockEmail: (email)   => api.post('/contacts/unblock/email', { email }),
  blockIp:      (ip)      => api.post('/contacts/block/ip',      { ip }),
  unblockIp:    (ip)      => api.post('/contacts/unblock/ip',    { ip }),
}

export const healthAPI = {
  check: () => api.get('/health'),
}

export default api
